import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, Typography, Button, Chip, Skeleton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  LinearProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import TimelineIcon from '@mui/icons-material/Timeline';
import BarChartIcon from '@mui/icons-material/BarChart';
import SchoolIcon from '@mui/icons-material/School';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import { calibrationAPI } from '../services/api';

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (v, d = 1) => v != null ? Number(v).toFixed(d) : '—';
const pct = (v, d = 1) => v != null ? `${(v * 100).toFixed(d)}%` : '—';
const signColor = (v) => v > 0 ? '#10b981' : v < 0 ? '#ef4444' : '#64748b';
const signPrefix = (v) => v > 0 ? '+' : '';

/* Shared styles */
const CARD_SX = { borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' };
const HEADER_SX = { px: 2.5, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#fafafa' };
const TH_SX = { fontWeight: 700, fontSize: '0.68rem', color: 'text.secondary', py: 1.25, bgcolor: '#fff', textTransform: 'uppercase', letterSpacing: '0.3px' };

// ── Page ─────────────────────────────────────────────────────────────────────
const CalibrationDashboard = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [health, setHealth] = useState(null);
  const [stats, setStats] = useState(null);
  const [currentCal, setCurrentCal] = useState(null);
  const [trades, setTrades] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [healthRes, statsRes, calRes, tradesRes] = await Promise.all([
        calibrationAPI.getHealth('scanner_patterns'),
        calibrationAPI.getStats('scanner_patterns'),
        calibrationAPI.getCurrent('scanner_patterns'),
        calibrationAPI.getTradesList('scanner_patterns', 50),
      ]);
      setHealth(healthRes.data);
      setStats(statsRes.data);
      setCurrentCal(calRes.data);
      setTrades(tradesRes.data?.trades || []);
    } catch (error) {
      console.error('Failed to fetch calibration data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpdateCalibration = async () => {
    try {
      setUpdating(true);
      await calibrationAPI.update('scanner_patterns', {
        min_trades_per_bucket: 5,
        lookback_days: 365,
        learning_rate: 0.3,
      });
      await fetchData();
    } catch (error) {
      console.error('Failed to update calibration:', error);
    } finally {
      setUpdating(false);
    }
  };

  const summary = stats?.summary || {};
  const winRate = summary.win_rate || 0;
  const totalTrades = summary.total_trades || 0;

  // ── Skeleton ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Skeleton variant="rounded" width={44} height={44} sx={{ borderRadius: '12px' }} />
            <Box>
              <Skeleton width={220} height={24} />
              <Skeleton width={200} height={14} sx={{ mt: 0.5 }} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Skeleton variant="rounded" width={90} height={36} sx={{ borderRadius: '10px' }} />
            <Skeleton variant="rounded" width={110} height={36} sx={{ borderRadius: '10px' }} />
          </Box>
        </Box>
        <Skeleton variant="rounded" height={100} sx={{ borderRadius: 3, mb: 2.5 }} />
        <Card sx={{ ...CARD_SX, mb: 2.5 }}>
          <Box sx={{ display: 'flex' }}>
            {[0, 1, 2].map(i => (
              <Box key={i} sx={{ flex: 1, p: 2.5, borderRight: i < 2 ? '1px solid #eee' : 'none' }}>
                <Skeleton width={120} height={16} sx={{ mb: 1.5 }} />
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  {[0, 1, 2].map(j => <Skeleton key={j} variant="rounded" height={38} sx={{ flex: 1, borderRadius: 1.5 }} />)}
                </Box>
                {[0, 1].map(j => (
                  <Box key={j} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                    <Skeleton width={90} height={14} />
                    <Skeleton variant="rounded" width={28} height={18} sx={{ borderRadius: 1 }} />
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Card>
        {[0, 1].map(i => (
          <Card key={i} sx={{ ...CARD_SX, mb: i === 0 ? 2.5 : 0 }}>
            <Box sx={HEADER_SX}>
              <Skeleton width={180} height={18} />
              <Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: 3 }} />
            </Box>
            <Box sx={{ p: 2 }}>
              {[0, 1, 2, 3].map(j => <Skeleton key={j} variant="rounded" height={38} sx={{ mb: 0.5, borderRadius: 1 }} />)}
            </Box>
          </Card>
        ))}
      </Box>
    );
  }

  // ── Health banner config ───────────────────────────────────────────────────
  const healthStatus = health?.status || 'unknown';
  const healthGradient = healthStatus === 'excellent'
    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    : healthStatus === 'good'
    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';

  const healthLabel = healthStatus === 'excellent' ? t('calibration.healthExcellent') : healthStatus === 'good' ? t('calibration.healthGood') : t('calibration.healthLearning');

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: '12px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 3px 10px rgba(102,126,234,0.25)',
          }}>
            <TrackChangesIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.2rem', letterSpacing: '-0.01em' }}>
              {t('calibration.title', 'Hiệu Chỉnh Xác Suất')}
            </Typography>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.15 }}>
              {t('calibration.subtitle', 'Tự động học từ kết quả giao dịch thực tế để cải thiện dự đoán')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={fetchData}
            disabled={updating}
            startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: '10px',
              borderColor: '#e2e8f0',
              color: '#64748b',
              fontWeight: 600,
              fontSize: '0.75rem',
              textTransform: 'none',
              px: 2,
              '&:hover': {
                borderColor: '#6366f1',
                color: '#6366f1',
                backgroundColor: 'rgba(99,102,241,0.04)',
              },
            }}
          >
            {t('common.refresh', 'Làm mới')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleUpdateCalibration}
            disabled={updating || totalTrades < 10}
            startIcon={<AutoFixHighIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: '10px',
              borderColor: updating ? '#667eea' : '#e2e8f0',
              color: updating ? '#667eea' : '#64748b',
              fontWeight: 600,
              fontSize: '0.75rem',
              textTransform: 'none',
              px: 2,
              '&:hover': {
                borderColor: '#667eea',
                color: '#667eea',
                backgroundColor: 'rgba(102,126,234,0.04)',
              },
            }}
          >
            {updating ? t('calibration.updating') : t('calibration.updateButton')}
          </Button>
        </Box>
      </Box>

      {/* ══════════════════════════════════════════════════════════════════
         SUMMARY BANNER — Health + 4 core KPIs (clean, not cramped)
         ══════════════════════════════════════════════════════════════════ */}
      <Box sx={{ mb: 2.5, borderRadius: 3, background: healthGradient, color: 'white', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
          {/* Health status + recommendation */}
          <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 1.5, flex: '0 0 260px', borderRight: '1px solid rgba(255,255,255,0.15)' }}>
            <HealthAndSafetyIcon sx={{ fontSize: 36, opacity: 0.9 }} />
            <Box>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, lineHeight: 1.15 }}>{healthLabel}</Typography>
              <Typography sx={{ fontSize: '0.62rem', opacity: 0.75, lineHeight: 1.3, mt: 0.25 }}>
                {t('calibration.mae')} {((health?.mean_absolute_error || 0) * 100).toFixed(1)}% · {health?.buckets_with_data || 0}/6 {t('calibration.buckets')}
              </Typography>
              {health?.recommendation && (
                <Typography sx={{ fontSize: '0.6rem', opacity: 0.85, lineHeight: 1.35, mt: 0.5, fontWeight: 500 }}>
                  {health.recommendation}
                </Typography>
              )}
            </Box>
          </Box>

          {/* 4 core KPIs — spacious, readable */}
          {[
            { label: t('calibration.kpi.trades'), big: totalTrades, sub: t('calibration.kpi.winLoss', { wins: summary.wins || 0, losses: summary.losses || 0 }) },
            { label: t('calibration.kpi.winRate'), big: `${(winRate * 100).toFixed(0)}%`, sub: `P: ${pct(currentCal?.p_min)}–${pct(currentCal?.p_max)}`, bar: winRate },
            { label: t('calibration.kpi.avgReturn'), big: `${signPrefix(summary.avg_return_pct)}${fmt(summary.avg_return_pct, 2)}%`, sub: t('calibration.kpi.avgWinLoss', { win: fmt(summary.avg_win_pct, 1), loss: fmt(summary.avg_loss_pct, 1) }), valueColor: (summary.avg_return_pct || 0) >= 0 },
            { label: t('calibration.kpi.avgHold'), big: `${fmt(summary.avg_hold_days, 1)}d`, sub: `${t('calibration.kpi.rMult')}: ${fmt(summary.avg_r_multiple, 2)}R · ${t('calibration.kpi.confidence')} ${((health?.confidence || 0) * 100).toFixed(0)}%` },
          ].map((m, i, arr) => (
            <Box key={i} sx={{
              flex: 1, px: 2.5, py: 2.5, display: 'flex', flexDirection: 'column', justifyContent: 'center',
              borderRight: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.12)' : 'none',
            }}>
              <Typography sx={{ fontSize: '0.58rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.7, mb: 0.5 }}>
                {m.label}
              </Typography>
              <Typography sx={{
                fontSize: '1.5rem', fontWeight: 800, lineHeight: 1.1,
                color: m.valueColor === false ? '#fecaca' : m.valueColor === true ? '#bbf7d0' : 'inherit',
              }}>
                {m.big}
              </Typography>
              {m.bar != null && (
                <LinearProgress variant="determinate" value={m.bar * 100}
                  sx={{ mt: 0.5, mb: 0.25, height: 3, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.15)', '& .MuiLinearProgress-bar': { bgcolor: '#bbf7d0', borderRadius: 2 } }}
                />
              )}
              <Typography sx={{ fontSize: '0.6rem', opacity: 0.65, mt: m.bar != null ? 0 : 0.25 }}>{m.sub}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ══════════════════════════════════════════════════════════════════
         INSIGHTS — Signal header + Exit/Accuracy panels in ONE card
         ══════════════════════════════════════════════════════════════════ */}
      <Card sx={{ ...CARD_SX, mb: 2.5 }}>
        {/* ── Signal strip as card header ─────────────────────────────── */}
        <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1.25, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#fafbfc' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
            <TrackChangesIcon sx={{ fontSize: '0.9rem', color: '#8b5cf6' }} />
            <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{t('calibration.signals.title')}</Typography>
          </Box>
          {(() => {
            const as = stats?.active_signals || {};
            const byStatus = as.by_status || {};
            return (
              <>
                <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, alignItems: 'baseline' }}>
                  {[
                    { label: t('calibration.signals.active'), value: byStatus.active || 0, color: '#3b82f6' },
                    { label: t('calibration.signals.closed'), value: byStatus.closed || 0, color: '#10b981' },
                    { label: t('calibration.signals.watch'), value: byStatus.watch || 0, color: '#f59e0b' },
                  ].map((s, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                      <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</Typography>
                      <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, color: 'text.secondary' }}>{s.label}</Typography>
                    </Box>
                  ))}
                </Box>
                {(as.by_pattern || []).length > 0 && (
                  <Box sx={{ borderLeft: '1px solid', borderColor: 'divider', pl: 1.25, display: 'flex', flexWrap: 'wrap', gap: 0.35, flex: 1, minWidth: 0, alignItems: 'center' }}>
                    {as.by_pattern.map((p, i) => (
                      <Typography key={i} component="span"
                        sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap' }}>
                        {p.pattern_type} <Box component="span" sx={{ fontWeight: 800, color: '#334155' }}>{p.count}</Box>
                        {i < (as.by_pattern.length - 1) ? <Box component="span" sx={{ mx: 0.4, color: '#cbd5e1' }}>·</Box> : ''}
                      </Typography>
                    ))}
                  </Box>
                )}
              </>
            );
          })()}
        </Box>

        {/* ── 2 panels: Exit Reasons | Accuracy ──────────────────────── */}
        <Box sx={{ display: 'flex' }}>

          {/* Left: Exit Reasons */}
          <Box sx={{ flex: '0 0 42%', p: 2, borderRight: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
              <TimelineIcon sx={{ fontSize: '0.95rem', color: '#f59e0b' }} />
              <Typography sx={{ fontWeight: 700, fontSize: '0.82rem' }}>{t('calibration.exitReasons.title')}</Typography>
            </Box>
            {(() => {
              const eb = stats?.exit_breakdown || {};
              const configs = {
                target: { label: t('calibration.exitReasons.target'), color: '#10b981', dot: '#10b981' },
                stop: { label: t('calibration.exitReasons.stop'), color: '#ef4444', dot: '#ef4444' },
                time: { label: t('calibration.exitReasons.time'), color: '#6b7280', dot: '#6b7280' },
              };
              return Object.keys(eb).length > 0 ? (
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    {Object.entries(eb).map(([reason, data], idx, arr) => {
                      const cfg = configs[reason] || configs.time;
                      return (
                        <Box component="tr" key={reason} sx={{ '& td': { py: 0.6, fontSize: '0.75rem', ...(idx < arr.length - 1 && { borderBottom: '1px solid #f1f5f9' }) } }}>
                          <Box component="td" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: cfg.dot, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: cfg.color }}>{cfg.label}</Typography>
                          </Box>
                          <Box component="td" sx={{ textAlign: 'right', fontWeight: 800, color: cfg.color, fontFamily: 'monospace' }}>{data.count}</Box>
                          <Box component="td" sx={{ textAlign: 'right', color: 'text.secondary', fontSize: '0.68rem' }}>
                            {signPrefix(data.avg_return)}{fmt(data.avg_return, 2)}%
                          </Box>
                          <Box component="td" sx={{ textAlign: 'right', color: 'text.secondary', fontSize: '0.68rem' }}>
                            {fmt(data.avg_hold, 0)}d
                          </Box>
                        </Box>
                      );
                    })}
                  </tbody>
                </Box>
              ) : (
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', textAlign: 'center', py: 2 }}>{t('calibration.exitReasons.noData')}</Typography>
              );
            })()}
          </Box>

          {/* Right: Calibration Accuracy */}
          <Box sx={{ flex: 1, minWidth: 0, p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <SchoolIcon sx={{ fontSize: '0.95rem', color: '#06b6d4' }} />
                <Typography sx={{ fontWeight: 700, fontSize: '0.82rem' }}>{t('calibration.accuracy.title')}</Typography>
              </Box>
              <Chip label={`MAE ${((health?.mean_absolute_error || 0) * 100).toFixed(1)}%`} size="small" sx={{ height: 18, fontWeight: 600, fontSize: '0.55rem', bgcolor: '#ecfdf5', color: '#065f46' }} />
            </Box>
            {(() => {
              const buckets = health?.accuracy_by_bucket || [];
              return buckets.length > 0 ? (
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                  <Box component="thead">
                    <Box component="tr" sx={{ '& th': { fontSize: '0.58rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', pb: 0.5, textAlign: 'left' } }}>
                      <Box component="th">{t('calibration.accuracy.bucket')}</Box>
                      <Box component="th" sx={{ textAlign: 'center !important' }}>{t('calibration.accuracy.trades')}</Box>
                      <Box component="th">{t('calibration.accuracy.predicted')}</Box>
                      <Box component="th">{t('calibration.accuracy.actual')}</Box>
                      <Box component="th" sx={{ textAlign: 'right !important' }}>{t('calibration.accuracy.error')}</Box>
                    </Box>
                  </Box>
                  <tbody>
                    {buckets.map((b, i) => (
                      <Box component="tr" key={i} sx={{ '& td': { py: 0.5, fontSize: '0.72rem', ...(i < buckets.length - 1 && { borderBottom: '1px solid #f1f5f9' }) } }}>
                        <Box component="td" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>
                          {b.bucket}–{Number(b.bucket) + 10}
                        </Box>
                        <Box component="td" sx={{ textAlign: 'center', color: 'text.secondary' }}>{b.trades}</Box>
                        <Box component="td">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: '#e2e8f0', overflow: 'hidden' }}>
                              <Box sx={{ width: `${b.predicted * 100}%`, height: '100%', bgcolor: '#667eea', borderRadius: 2 }} />
                            </Box>
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#667eea', fontFamily: 'monospace' }}>{(b.predicted * 100).toFixed(0)}%</Typography>
                          </Box>
                        </Box>
                        <Box component="td">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: '#e2e8f0', overflow: 'hidden' }}>
                              <Box sx={{ width: `${b.actual * 100}%`, height: '100%', bgcolor: '#10b981', borderRadius: 2 }} />
                            </Box>
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#10b981', fontFamily: 'monospace' }}>{(b.actual * 100).toFixed(0)}%</Typography>
                          </Box>
                        </Box>
                        <Box component="td" sx={{ textAlign: 'right' }}>
                          <Chip label={`${(b.error * 100).toFixed(0)}%`} size="small"
                            sx={{ height: 16, fontSize: '0.52rem', fontWeight: 700, bgcolor: b.error < 0.1 ? '#d1fae5' : '#fef3c7', color: b.error < 0.1 ? '#065f46' : '#92400e' }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </tbody>
                </Box>
              ) : (
                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', textAlign: 'center', py: 1.5 }}>
                  {t('calibration.accuracy.needMoreTrades')}
                </Typography>
              );
            })()}
          </Box>

        </Box>
      </Card>

      {/* ══════════════════════════════════════════════════════════════════
         TABLES — stacked vertically for full width
         ══════════════════════════════════════════════════════════════════ */}

      {/* ── Pattern Performance (full width) ───────────────── */}
      <Card sx={{ ...CARD_SX, mb: 2.5 }}>
            <Box sx={HEADER_SX}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BarChartIcon sx={{ fontSize: '1rem', color: '#667eea' }} />
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{t('calibration.pattern.title')}</Typography>
              </Box>
              <Chip label={t('calibration.pattern.count', { count: (stats?.pattern_breakdown || []).length })} size="small" sx={{ fontWeight: 600, fontSize: '0.62rem', bgcolor: '#ede9fe', color: '#6d28d9' }} />
            </Box>
            {(stats?.pattern_breakdown || []).length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': TH_SX }}>
                      <TableCell>{t('calibration.pattern.column')}</TableCell>
                      <TableCell align="center">{t('calibration.pattern.trades')}</TableCell>
                      <TableCell align="center" sx={{ minWidth: 110 }}>{t('calibration.pattern.winRate')}</TableCell>
                      <TableCell align="right">{t('calibration.pattern.avgReturn')}</TableCell>
                      <TableCell align="center">{t('calibration.pattern.rMult')}</TableCell>
                      <TableCell align="center">{t('calibration.pattern.hold')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.pattern_breakdown.map((p, i) => {
                      const wr = (p.win_rate * 100).toFixed(0);
                      const wrColor = p.win_rate >= 0.5 ? '#10b981' : p.win_rate >= 0.35 ? '#f59e0b' : '#ef4444';
                      return (
                        <TableRow key={i} sx={{ '& td': { py: 1, borderColor: '#f5f5f5' }, '&:hover': { bgcolor: '#fafafa' } }}>
                          <TableCell>
                            <Chip label={p.pattern_type} size="small" sx={{ fontWeight: 600, fontSize: '0.65rem', bgcolor: '#f1f5f9', height: 22 }} />
                          </TableCell>
                          <TableCell align="center">
                            <Typography sx={{ fontWeight: 700, fontSize: '0.82rem' }}>{p.total}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
                              <Box sx={{ width: 42, height: 6, borderRadius: 3, bgcolor: '#e8e8e8', overflow: 'hidden' }}>
                                <Box sx={{ width: `${wr}%`, height: '100%', bgcolor: wrColor, borderRadius: 3 }} />
                              </Box>
                              <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: wrColor, fontFamily: 'monospace', minWidth: 28 }}>{wr}%</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', fontFamily: 'monospace', color: signColor(p.avg_return) }}>
                              {signPrefix(p.avg_return)}{fmt(p.avg_return, 2)}%
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace', color: p.avg_r >= 0 ? '#667eea' : '#ef4444' }}>
                              {fmt(p.avg_r, 2)}R
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>{fmt(p.avg_hold, 1)}d</Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                <BarChartIcon sx={{ fontSize: 36, mb: 1, opacity: 0.3 }} />
                <Typography sx={{ fontSize: '0.82rem' }}>{t('calibration.pattern.noData')}</Typography>
              </Box>
            )}
      </Card>

      {/* ── Trade History (full width) ────────────────────────── */}
      <Card sx={CARD_SX}>
            <Box sx={HEADER_SX}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TimelineIcon sx={{ fontSize: '1rem', color: '#667eea' }} />
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{t('calibration.tradeHistory.title')}</Typography>
              </Box>
              <Chip label={t('calibration.tradeHistory.count', { count: trades.length })} size="small" sx={{ fontWeight: 600, fontSize: '0.62rem', bgcolor: '#ede9fe', color: '#6d28d9' }} />
            </Box>
            {trades.length > 0 ? (
              <TableContainer sx={{ maxHeight: 460 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ '& th': TH_SX }}>
                      <TableCell>{t('calibration.tradeHistory.ticker')}</TableCell>
                      <TableCell>{t('calibration.tradeHistory.pattern')}</TableCell>
                      <TableCell align="right">{t('calibration.tradeHistory.return')}</TableCell>
                      <TableCell align="center">{t('calibration.tradeHistory.rMultiple')}</TableCell>
                      <TableCell align="center">{t('calibration.tradeHistory.hold')}</TableCell>
                      <TableCell align="center">{t('calibration.tradeHistory.exit')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trades.map((trade, i) => {
                      const ret = trade.return_pct || 0;
                      return (
                        <TableRow key={i} sx={{ '& td': { py: 0.75, borderColor: '#f5f5f5' }, '&:hover': { bgcolor: '#fafafa' } }}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {trade.win
                                ? <CheckCircleIcon sx={{ fontSize: 12, color: '#10b981' }} />
                                : <CancelIcon sx={{ fontSize: 12, color: '#ef4444' }} />
                              }
                              <Typography sx={{ fontWeight: 700, fontSize: '0.78rem' }}>{trade.ticker}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', fontWeight: 500 }}>{trade.pattern_type}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', fontFamily: 'monospace', color: signColor(ret) }}>
                              {signPrefix(ret)}{fmt(ret, 2)}%
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, fontFamily: 'monospace', color: (trade.r_multiple || 0) >= 0 ? '#10b981' : '#ef4444' }}>
                              {fmt(trade.r_multiple, 2)}R
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{trade.hold_days}d</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={trade.exit_reason === 'target' ? t('calibration.tradeHistory.exitTarget') : trade.exit_reason === 'stop' ? t('calibration.tradeHistory.exitStop') : t('calibration.tradeHistory.exitTime')} size="small"
                              sx={{
                                height: 18, fontSize: '0.55rem', fontWeight: 600,
                                bgcolor: trade.exit_reason === 'target' ? '#dbeafe' : trade.exit_reason === 'stop' ? '#fee2e2' : '#f3f4f6',
                                color: trade.exit_reason === 'target' ? '#1e40af' : trade.exit_reason === 'stop' ? '#991b1b' : '#6b7280',
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                <TimelineIcon sx={{ fontSize: 36, mb: 1, opacity: 0.3 }} />
                <Typography sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{t('calibration.tradeHistory.noData')}</Typography>
              </Box>
            )}
      </Card>
    </Box>
  );
};

export default CalibrationDashboard;
